import React, {
  CSSProperties,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import {
  PayoutObservation,
  PayoutObservationContext,
  reconcilePayoutObservations
} from 'contexts/PayoutObservationContext';
import { ViewManagerContext } from 'contexts/ViewManagerContext';
import { useSupportsHover } from 'hooks/useSupportsHover';
import { useTrackpadTableSwipe } from 'hooks/useTrackpadTableSwipe';
import { useViewportWidth } from 'hooks/useViewportWidth';
import { isSwipeIgnoredTarget } from 'utils/swipeIgnore';
import { clampTablesInView, TILED_TABLE_VIEW_ENABLED } from 'utils/tableViewWidth';

import {
  applyEdgeResistance,
  decideSwipe,
  DRAG_START_PX
} from 'components/RebindingTableView/swipeCommit';
import {
  pushTouchSample,
  releaseVelocityPxPerMs,
  type TouchSample
} from 'components/RebindingTableView/swipeVelocity';
import {
  beginSwipeDrag,
  endSwipeDrag,
  getDragState,
  isSwipeSettling,
  updateSwipeDrag
} from 'components/RebindingTableView/tableSwipeDrag';
import {
  beginTableSwitch,
  isTableSwitching,
  prefersReducedMotion
} from 'components/RebindingTableView/tableSwitchTransition';
import { useActiveTables } from 'components/SaloonLobby/useActiveTables';

import { resolvePanelMotion } from './viewTransition';

type SlideContainerProps = {
  lobbyPanel: React.ReactNode;
  renderTablePanel: () => React.ReactNode;
  gridPanel?: React.ReactNode;
};

// A table-switch flick must be a clear horizontal gesture: at least this many
// px of horizontal travel, and meaningfully more horizontal than vertical so a
// diagonal scroll doesn't trigger it. (Only the no-drag fallback path below
// still uses these; a tracked drag decides via decideSwipe.)
const SWIPE_MIN_X = 60;
const SWIPE_H_OVER_V = 1.5;

/**
 * Backstop for clearing a staged cut when requestAnimationFrame never runs
 * (a backgrounded tab pauses it). Deliberately far longer than the two
 * frames a staged cut normally takes (~33ms) so the rAF path always wins on
 * a live tab and the flip timing that keeps the backdrop hidden is unchanged.
 */
const CUT_STAGE_FAILSAFE_MS = 250;

// COMMIT_FRACTION / FLICK_PX / FLICK_MS / EDGE_RESISTANCE moved to
// RebindingTableView/swipeCommit.ts and were replaced by momentum projection —
// one continuous rule instead of a distance threshold OR'd with a flick
// threshold. See that file for why; DRAG_START_PX and the edge rubber band now
// come from there too, so touch and trackpad share every constant.

/**
 * Three stacked panels: lobby, single table, and multi-table grid.
 * Uses CSS transitions driven by activeView — no GSAP, immune to re-render interruptions.
 */
const SlideContainer = ({ lobbyPanel, renderTablePanel, gridPanel }: SlideContainerProps) => {
  const { activeView, activeTableInfo, isTableMounted, tablesInView, tableWindowStart } =
    useContext(ViewManagerContext);
  const payoutObservations = useRef(new Map<string, PayoutObservation>()).current;
  const previousPayoutView = useRef<{ view: typeof activeView; table: string }>({
    view: activeView,
    table: String(activeTableInfo?.gameID ?? '')
  });
  useLayoutEffect(() => {
    const table = String(activeTableInfo?.gameID ?? '');
    const previous = previousPayoutView.current;
    reconcilePayoutObservations(payoutObservations, previous, { view: activeView, table });
    previousPayoutView.current = { view: activeView, table };
  }, [activeView, activeTableInfo?.gameID, payoutObservations]);
  const viewportWidth = useViewportWidth();
  // Same ordered list + opener the YourTables fan uses, so a swipe steps through
  // tables in the exact left→right order the player sees in the fan.
  const { activeTables, openTable } = useActiveTables();
  const touchStart = useRef<{ x: number; y: number; ignore: boolean; at: number } | null>(null);
  // Recent touch samples, so a release can be projected under inertia instead
  // of judged on distance alone. A RING of samples rather than the single
  // previous point this used to keep: with four tables open the main thread
  // stalls long enough that the browser coalesces touchmoves away, and one
  // pair plus a "was the last event over 100ms ago?" pause test could not tell
  // a flick through a stall from a finger that stopped. See swipeVelocity.
  const moveSamples = useRef<TouchSample[]>([]);
  const velocity = useRef(0);
  // The table a just-committed gesture is heading to. neighbours() computes
  // from ViewManager React state (tableWindowStart/activeTableInfo), which
  // lags openTable by a render - a fast SECOND swipe started during the
  // settle would read the OLD window and commit relative to the table the
  // player already left, visibly bouncing them backwards. This ref is the
  // gesture layer's own synchronous record of where it just sent the player;
  // TTL'd well past state propagation so a stale value can never linger.
  const pendingCommit = useRef<{ gid: string; at: number } | null>(null);
  const markCommit = (gid: string) => {
    pendingCommit.current = { gid: String(gid), at: performance.now() };
  };
  // The table surface — the trackpad gesture reads wheel events from it.
  const tableSurfaceRef = useRef<HTMLDivElement>(null);
  // Set once a touch has been recognised as a horizontal drag: the neighbours
  // are on screen and the track is following the finger.
  const dragging = useRef(false);
  // Offset the felt was already sitting at when this drag began. Zero from
  // rest; non-zero when the player grabbed a spring-back in flight, so their
  // travel adds to where the felt actually is rather than teleporting it to a
  // panel boundary under their finger.
  const carry = useRef(0);

  // Horizontal swipe to switch between the player's open tables (Unity parity).
  // Swipe left → the next table to the right in the fan; swipe right → the
  // previous one. Clamped at both ends (no wrap-around).
  const handleTableTouchStart = (e: React.TouchEvent) => {
    // A NEW gesture inherits NOTHING from the last one. The samples were only
    // cleared on touchend's commit path, so every interrupted drag left its own
    // behind: an OS cancel, a second finger mid-drag, or a release that bailed on
    // one of touchend's guards. releaseVelocityPxPerMs anchors on the newest
    // sample still inside VELOCITY_MAX_LOOKBACK_MS, so a retry that began before
    // that window closed could anchor on the PREVIOUS gesture's coordinates - and
    // since the finger may come down far from where it left, that reads as a large
    // velocity in the WRONG direction and commits the opposite table off a short
    // drag. Same player-facing failure this file exists to stop, so reset on entry
    // rather than trusting every exit path to do it.
    moveSamples.current = [];
    velocity.current = 0;
    // Second finger down (pinch-zoom, two-finger scroll): abandon any drag and
    // hand the gesture back. On a real device these are common near the felt,
    // and treating one as a swipe switches tables under the player.
    if (e.touches.length > 1) {
      if (dragging.current) endSwipeDrag(null);
      dragging.current = false;
      touchStart.current = null;
      return;
    }
    const t = e.touches[0];
    if (!t) return;
    // Ignore gestures that begin somewhere owning its own: the raise-amount
    // slider, any text field, or anything opting out via data-swipe-ignore —
    // which is how the chat and table-history panels keep their scroll. Shared
    // with the trackpad Observer's ignoreCheck so both input paths agree on one
    // rule; see utils/swipeIgnore.
    const ignore = isSwipeIgnoredTarget(e.target);
    // e.timeStamp, not Date.now(): the event's creation time survives main-
    // thread stalls; handler-run time does not (see the velocity comment below).
    touchStart.current = { x: t.clientX, y: t.clientY, ignore, at: e.timeStamp };
    dragging.current = false;
  };

  /**
   * What a page would land on, either side of the WINDOW currently on screen.
   *
   * Not "either side of the focused table" — with two felts up, the table you
   * are focused on may be the right-hand one while the window is already hard
   * against the start of the list, so there is nothing to page left to. Asking
   * the window rather than the focus is what stops a swipe committing to a
   * table that cannot come on screen.
   *
   * `prev`/`next` are the tables that would become the new window START, so a
   * page always moves by exactly one table however many are in view.
   */
  const neighbours = () => {
    const total = activeTables.length;
    if (!total) return null;
    // Mirrors Game's gate exactly: while tiling is parked the carousel shows one
    // felt, so the gesture must page relative to a one-table window — a stored
    // "3 tables" preference must not strand the swipe against a phantom window.
    const perView = TILED_TABLE_VIEW_ENABLED
      ? clampTablesInView(tablesInView, viewportWidth, total)
      : 1;
    let start = Math.min(Math.max(0, tableWindowStart), Math.max(0, total - perView));
    const pending = pendingCommit.current;
    if (pending) {
      if (
        performance.now() - pending.at > 1500 ||
        String(activeTables[start]?.gameID) === pending.gid
      ) {
        // State caught up (or the record aged out) - back to the real source.
        pendingCommit.current = null;
      } else {
        const idx = activeTables.findIndex((t) => String(t.gameID) === pending.gid);
        if (idx >= 0) start = Math.min(Math.max(0, idx), Math.max(0, total - perView));
      }
    }
    if (!activeTables[start]) return null;
    return {
      cur: activeTables[start],
      prev: start > 0 ? activeTables[start - 1] : null,
      // Only if there is still an unseen table to reveal on the right.
      next: start + perView < total ? activeTables[start + 1] : null
    };
  };

  // `(hover: hover)` — a real pointer, not a finger. Gates the trackpad path.
  const supportsHover = useSupportsHover();

  const canDrag = () =>
    activeView === 'table' &&
    !!activeTableInfo &&
    activeTables.length >= 2 &&
    !prefersReducedMotion();

  // Trackpad / Magic Mouse horizontal swipe — the same gesture as the finger,
  // for the input desktop actually has. Feeds the identical drag channel, so
  // both share one commit rule, one settle and one set of edge behaviours.
  // Deliberately declared with the touch handlers rather than beside them in
  // the JSX: React's synthetic onWheel is always passive, so the listener has
  // to be attached to the node directly (Observer does that) to suppress
  // Safari's two-finger history swipe-back.
  useTrackpadTableSwipe({
    target: tableSurfaceRef,
    // Only things a RENDER can change gate the listener. Whether a switch is
    // mid-flight changes without one, so that check belongs in onDragStart,
    // which runs at the moment the gesture actually starts.
    //
    // POINTER DEVICES ONLY — two separate bugs land on this one gate.
    //
    // EN-10644 (double commit): a touch device already has a complete gesture
    // in the touch handlers below, and running both against one finger made
    // every swipe commit TWICE — the second commit resolving neighbours()
    // after markCommit had already moved the position, so it paged straight
    // back to where the player started. Measured on device: the touch commit
    // at 0ms, the Observer's at +23ms, the URL following it back at +56ms
    // (2026-09-09). The hook's own pointerBlocked guard cannot cover this
    // alone — see useTrackpadTableSwipe.
    //
    // EN-10653 (chat / history not scrollable): the Observer subscribes
    // non-passive `pointer*` listeners with `preventDefault: true` on
    // tableSurfaceRef to suppress Safari's history swipe-back. Chat and
    // table-history panels are `position: fixed` DESCENDANTS of that node, so
    // their pointer events bubble through it and that preventDefault killed
    // native vertical scroll — "chat and history stop scrolling once a second
    // table is observed", the boundary being exactly where `enabled` flips
    // true. preventDefault runs at the Observer level BEFORE pointerBlocked is
    // consulted, which is why the guard alone cannot fix this one either.
    enabled: canDrag() && supportsHover,
    getNeighbours: useCallback(() => {
      const around = neighbours();
      if (!around) return null;
      return {
        prev: around.prev?.gameID ?? null,
        cur: around.cur.gameID,
        next: around.next?.gameID ?? null
      };
      // neighbours() closes over activeTables/activeTableInfo, which is exactly
      // what should re-arm the gesture when the open-table set changes.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTables, activeTableInfo]),
    // Returns the offset to add this gesture's travel to, or null to refuse it
    // outright — same rule as the touch path.
    onDragStart: useCallback((session) => {
      // ANY settle is grabbable now — spring-backs keep their session, a
      // committed settle re-bases into the new table's frame (beginSwipeDrag).
      // The only refusal left is a programmatic tap-slide (260ms, no drag
      // session on the track to take over).
      if (isTableSwitching() && !isSwipeSettling()) return null;
      return beginSwipeDrag(session);
    }, []),
    onDragMove: useCallback((dx: number) => updateSwipeDrag(dx), []),
    onDragEnd: useCallback(
      (targetId: string | null, velocityPxPerMs: number) => {
        endSwipeDrag(targetId, velocityPxPerMs);
        if (!targetId) return;
        const target = activeTables.find((t) => t.gameID === targetId);
        // forceSingle: a swipe moves BETWEEN tables inside single-table view —
        // it must never re-route the view. Without it, a sticky multi-table
        // preference made openTable send an already-open table to /grid, so
        // the first swipe after adding a table flipped the player to the grid
        // instead of sliding (Jason on test, 8/20). Routing-only: forceSingle
        // never writes the preference. fromGesture + markCommit are the
        // EN-10538 half: the commit registers with the gesture ledger and is
        // never swallowed by an in-flight cut.
        if (target) {
          markCommit(target.gameID);
          openTable(target.gameID, target.game, 'no', { forceSingle: true, fromGesture: true });
        }
      },
      [activeTables, openTable]
    )
  });

  // Follow the finger. The first qualifying move brings the neighbouring
  // tables on screen; every move after that just writes an offset (the
  // carousel applies it straight to the DOM — no React work per frame).
  const handleTableTouchMove = (e: React.TouchEvent) => {
    const start = touchStart.current;
    if (!start || start.ignore || !canDrag()) return;
    // A finger joined mid-drag — same reasoning as touchstart.
    if (e.touches.length > 1) {
      if (dragging.current) endSwipeDrag(null);
      dragging.current = false;
      touchStart.current = null;
      carry.current = 0;
      return;
    }
    const t = e.touches[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;

    if (!dragging.current) {
      if (Math.abs(dx) < DRAG_START_PX || Math.abs(dx) < Math.abs(dy)) return;
      const around = neighbours();
      if (!around || (!around.prev && !around.next)) return;
      // ANY settle is grabbable now (spring-back keeps its session; a committed
      // settle re-bases into the new table's frame — see beginSwipeDrag). Only
      // a programmatic tap-slide still refuses: it has no drag session on the
      // track to take over, and it's over in 260ms.
      if (isTableSwitching() && !isSwipeSettling()) return;
      dragging.current = true;
      carry.current = beginSwipeDrag({
        prev: around.prev?.gameID ?? null,
        cur: around.cur.gameID,
        next: around.next?.gameID ?? null
      });
    }

    // Sample recent motion so the release has a real velocity to project and
    // to size the settle with. One previous point is enough and is what the
    // projection wants: the speed at the moment of release, not the average
    // over the whole drag (a slow drag ending in a flick must read as a flick).
    //
    // TIMING - e.timeStamp (when the touch HAPPENED), never Date.now() (when
    // this handler finally RAN). Under a main-thread stall touch events queue
    // and then flush in a burst: two moves 30ms apart in the world process
    // ~1ms apart in the handler, and wall-clock dt makes the computed velocity
    // garbage in either direction. A deflated read turns a decisive flick into
    // a spring-back ("the flick failed"); an inflated one commits a gesture
    // the player never made. Event timestamps are immune to processing delay.
    moveSamples.current = pushTouchSample(moveSamples.current, { x: t.clientX, at: e.timeStamp });

    const around = neighbours();
    // Where the felt actually is: this drag's travel added to wherever it was
    // when the finger landed (see `carry`).
    const offset = carry.current + dx;
    // Rubber-band when there is nothing that way: the drag still responds, it
    // just resists — and the resistance GROWS with distance, so the edge reads
    // as a physical limit rather than a slower drag.
    const blocked = (offset < 0 && !around?.next) || (offset > 0 && !around?.prev);
    updateSwipeDrag(blocked ? applyEdgeResistance(offset, window.innerWidth) : offset);
  };

  // A cancelled touch is not a decision: the OS took the gesture (call, system
  // edge-swipe, multi-touch). Always spring back to the current table — never
  // commit a switch the player never finished making.
  const handleTableTouchCancel = () => {
    const wasDragging = dragging.current;
    dragging.current = false;
    touchStart.current = null;
    carry.current = 0;
    // The OS took the gesture. Drop its samples too - see handleTableTouchStart.
    moveSamples.current = [];
    velocity.current = 0;
    if (wasDragging) endSwipeDrag(null);
  };

  const handleTableTouchEnd = (e: React.TouchEvent) => {
    const start = touchStart.current;
    touchStart.current = null;
    const wasDragging = dragging.current;
    dragging.current = false;
    if (!start || start.ignore || activeView !== 'table' || !activeTableInfo) {
      carry.current = 0;
      if (wasDragging) endSwipeDrag(null);
      return;
    }
    if (activeTables.length < 2) {
      // A live drag must ALWAYS be closed out, whatever made it moot (here:
      // the open-table set shrank under the finger). Returning with the drag
      // phase still 'dragging' leaves the carousel's switch lock armed for its
      // full 60s deadline - header gone, gestures refused - until it expires.
      carry.current = 0;
      if (wasDragging) endSwipeDrag(null);
      return;
    }

    const end = e.changedTouches[0];
    const dx = end.clientX - start.x;
    const dy = end.clientY - start.y;

    // --- Released from a live drag: commit to the table the finger carried in,
    // or spring back. The carousel animates from wherever the finger left it.
    if (wasDragging) {
      // Measured across the release point, which `touchend` reports at the
      // position the finger actually lifted at — trustworthy even when every
      // intermediate move was coalesced away by a stall. A finger that stopped
      // and a finger that flicked through a stall differ in DISTANCE, not in
      // elapsed time, so no pause heuristic is needed: the arithmetic separates
      // them. See swipeVelocity for the full reasoning.
      velocity.current = releaseVelocityPxPerMs(moveSamples.current, {
        x: end.clientX,
        at: e.timeStamp
      });
      // Still nothing when NO touchmove was delivered at all (a browser that
      // skips them entirely, or a stall that swallowed the lot). Fall back to
      // the whole-gesture average, which for a genuine flick IS high and for a
      // slow drag is correctly low.
      if (velocity.current === 0) {
        const gestureMs = e.timeStamp - start.at;
        if (gestureMs > 0 && gestureMs < 300) {
          velocity.current = dx / gestureMs;
        }
      }
      const around = neighbours();
      // ONE rule, shared with the trackpad path: project where the drag would
      // land under inertia and commit if that clears half a table. Distance
      // and speed stop being separate thresholds, which is what removes the
      // "sometimes oversensitive, sometimes unresponsive" feel of the old
      // 25%-OR-45px-flick pair.
      const decision = decideSwipe(
        carry.current + dx,
        velocity.current,
        window.innerWidth || 1,
        Boolean(around?.prev),
        Boolean(around?.next)
      );
      const target =
        decision.direction === 1 ? around?.next : decision.direction === -1 ? around?.prev : null;
      carry.current = 0;
      // Hand the carousel its settle target BEFORE switching tables, so the
      // gameID change is recognised as this gesture's and doesn't kick off a
      // second animation on top of the settle.
      endSwipeDrag(target ? target.gameID : null, velocity.current);
      // forceSingle for the same reason as the trackpad commit below: a swipe
      // switches tables, never views. markCommit + fromGesture per EN-10538.
      if (target) {
        markCommit(target.gameID);
        openTable(target.gameID, target.game, 'no', { forceSingle: true, fromGesture: true });
      }
      moveSamples.current = [];
      velocity.current = 0;
      return;
    }

    // --- No drag was tracked (a browser that doesn't deliver touchmove,
    // reduced motion - or a gesture that began while a COMMITTED settle was in
    // flight, which the move handler refuses to track live). That last case is
    // the rapid re-swipe: the player pages twice quickly and the second flick
    // used to be swallowed here, landing them one table short "more often the
    // faster you swipe". A gesture-owned settle is safe to page from - the
    // committed switch already published, so neighbours() (pending-commit
    // aware) resolves against the RIGHT window, and the carousel treats a
    // gameID change mid-settle as this gesture's own (no stacked animation; it
    // cuts to the queued table when the settle lands). Only a PROGRAMMATIC
    // slide (nav-strip tap - drag phase idle) still drops the gesture.
    if (isTableSwitching() && getDragState().phase !== 'settling') return;
    if (Math.abs(dx) < SWIPE_MIN_X || Math.abs(dx) < Math.abs(dy) * SWIPE_H_OVER_V) return;

    // Resolve position through neighbours(), NOT activeTableInfo: mid-settle
    // the React-state table is still the one being LEFT, so indexing off it
    // made a chained second flick "commit" to the table the first flick was
    // already settling onto - a silent no-op, the lost swipe of a rapid pair
    // (reproduced: a chained double advanced one table, not two). neighbours()
    // is pending-commit aware and resolves against where the player is
    // actually heading; edges fall out naturally (no neighbour -> no page).
    const around = neighbours();
    if (!around) return;
    const target = dx < 0 ? around.next : around.prev;
    if (!target) return; // no table that way - the edge, not a failure
    markCommit(target.gameID);
    openTable(target.gameID, target.game, 'no', { forceSingle: true, fromGesture: true });
  };

  // The wrapper + panels are overflow:hidden and must NEVER hold a scroll
  // offset. But overflow:hidden does NOT block *programmatic* scrolling: when a
  // game event hits the always-mounted background table, a focus()/
  // scrollIntoView() on some element makes the browser scroll the nearest
  // scrollable ancestor (one of these shell divs) to "reveal" it — dragging the
  // whole lobby up by that scrollTop (the lobby-jump bug; confirmed: scnT === the
  // div's scrollTop). Pin any such scroll straight back to 0. (overflow:clip
  // would also prevent it, but the scroll-reset is universally supported.)
  const pinScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop !== 0) el.scrollTop = 0;
    if (el.scrollLeft !== 0) el.scrollLeft = 0;
  };

  // Derive panel positions from activeView.
  //
  // The lobby panel intentionally NEVER moves. It's unmounted when inactive
  // (see the conditional render below), so a slide-out animation buys nothing
  // visually — but it actively hurts: on return, a burst of lobby/table
  // WebSocket re-renders lands during the slide-in transition, and iOS WebView
  // recomputes the in-flight `translate3d(%)` on each repaint, leaving the
  // panel parked at varying partial offsets. Keeping the lobby pinned at 0
  // removes that whole failure mode — the table/grid panels slide OVER a
  // stationary lobby instead, covered via z-index.
  // One mover per transition (EN-10545): the entering view slides in over the
  // stationary current one, on its own home axis — see viewTransition.ts for
  // the full design rule. `prevViewRef` holds the last DISTINCT view and only
  // advances when the view actually changes: an effect-based update would
  // reset it on the next re-render, flipping the held panel to its rest
  // position while the 350ms transition is still running (and game ticks
  // re-render constantly). A panel parked at the origin stays parked — it sits
  // invisibly under the opaque active panel, which is exactly the hold this
  // motion needs.
  const prevViewRef = useRef<typeof activeView | null>(null);
  const lastViewRef = useRef(activeView);
  if (lastViewRef.current !== activeView) {
    prevViewRef.current = lastViewRef.current;
    lastViewRef.current = activeView;
  }
  // DOUBLE-BUFFERED CUT — every view change (round 3 of the flash hunt built
  // this for table↔grid; 8/21 generalized it to the lobby too, which killed
  // the last travel slides). The entering panel may be mounting its whole
  // subtree — the felt skeleton on table entry, the full lobby after its
  // unmounted-while-inactive gap — and its first frames must never paint. So
  // the swap waits: while staging, the LEAVING view keeps the top z (still
  // live, fully painted) and the entering view mounts UNDERNEATH it. Two
  // animation frames after the view change — one for React's commit to paint,
  // one for safety — the z-order flips. The player never sees anything but
  // two finished views; the backdrop can never show (the tap-"+"-for-lobby
  // brown-void, Jason 8/21).
  const [cutStaged, setCutStaged] = useState(false);
  useLayoutEffect(() => {
    // First mount: nothing to hold on top.
    if (prevViewRef.current === null) {
      setCutStaged(false);
      return undefined;
    }
    setCutStaged(true);
    // Entering the single-table view from ANYWHERE skips Game's entrance
    // fade — the cut needs the felt fully opaque at the flip.
    if (activeView === 'table') beginTableSwitch();
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setCutStaged(false));
    });
    // Safety net. While a cut is staged the view being LEFT holds the top slot
    // and the entering view carries pointerEvents:'none' — so if these frames
    // never run, the surface the player is looking at silently stops taking
    // input. requestAnimationFrame does NOT fire in a backgrounded tab, which
    // is exactly the case (switch views, tab away, come back). Well clear of
    // two frames (~33ms), so a normal cut always resolves through rAF and this
    // never changes the timing that keeps the backdrop hidden.
    const failsafe = setTimeout(() => setCutStaged(false), CUT_STAGE_FAILSAFE_MS);
    return () => {
      cancelAnimationFrame(first);
      if (second) cancelAnimationFrame(second);
      clearTimeout(failsafe);
    };
    // Re-stage on every view change, including grid→table→grid round trips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);
  // Who paints on top: normally the active view; while a cut is staged, the
  // view we are LEAVING (prevViewRef) keeps the top slot.
  const staged = cutStaged && prevViewRef.current !== null;
  const topView = staged ? prevViewRef.current : activeView;
  const motion = resolvePanelMotion(activeView, prevViewRef.current, staged);
  // Every transition is already a cut, so reduced motion needs no special
  // handling here (EN-10545 AC3 satisfied by construction).
  const { tableTransform, gridTransform, tableOpacity, gridOpacity } = motion;
  const tableTransition = motion.tableTransition;
  const gridTransition = motion.gridTransition;

  return (
    <div style={wrapperStyle} onScroll={pinScroll}>
      {/* Lobby panel — unmounted when not on screen so its decorative GSAP
          loops (tournament-row shimmers, day-carousel pulses, etc.) don't run
          invisibly while the player is at a table. "On screen" includes the
          staged frames when the lobby is the LEAVING view: it must keep
          painting on top until the entering table/grid has painted beneath,
          or the cut would flash the backdrop where the lobby just was. It is
          pinned at the origin and never transformed — the table/grid panels
          cover it by z-index, so the post-return WebSocket re-render burst
          has no in-flight transition to perturb. No `will-change` either — a
          stationary, heavily re-rendering subtree shouldn't hold a permanent
          compositor layer. */}
      <div
        style={{
          ...panelStyle,
          zIndex: topView === 'lobby' ? 2 : 1,
          pointerEvents: activeView === 'lobby' && topView === 'lobby' ? 'auto' : 'none'
        }}
        onScroll={pinScroll}>
        {(activeView === 'lobby' || (staged && prevViewRef.current === 'lobby')) && lobbyPanel}
      </div>

      {/* Single table panel */}
      <div
        style={{
          ...panelStyle,
          ...tableSwipeSurfaceStyle,
          transition: tableTransition,
          transform: tableTransform,
          opacity: tableOpacity,
          zIndex: topView === 'table' ? 2 : 1,
          pointerEvents: activeView === 'table' && topView === 'table' ? 'auto' : 'none'
        }}
        ref={tableSurfaceRef}
        onScroll={pinScroll}
        onTouchStart={handleTableTouchStart}
        onTouchMove={handleTableTouchMove}
        onTouchEnd={handleTableTouchEnd}
        onTouchCancel={handleTableTouchCancel}>
        {/* Stable key (was the gameID/tableKey): the table panel must stay
            mounted while the shown table changes so RebindingTableView can
            cross-fade the swap. Per-table remount/reset now happens one level
            down, on the gameID-keyed GameProvider inside Game. */}
        <PayoutObservationContext.Provider
          value={{
            observations: payoutObservations,
            surface: 'table',
            active: activeView === 'table'
          }}>
          {isTableMounted && (
            <React.Fragment key='single-table-panel'>{renderTablePanel()}</React.Fragment>
          )}
        </PayoutObservationContext.Provider>
      </div>

      {/* Multi-table grid panel */}
      {gridPanel && (
        <div
          style={{
            ...panelStyle,
            transition: gridTransition,
            transform: gridTransform,
            opacity: gridOpacity,
            zIndex: topView === 'grid' ? 2 : 1,
            pointerEvents: activeView === 'grid' && topView === 'grid' ? 'auto' : 'none'
          }}
          onScroll={pinScroll}>
          <PayoutObservationContext.Provider
            value={{
              observations: payoutObservations,
              surface: 'grid',
              active: activeView === 'grid'
            }}>
            {gridPanel}
          </PayoutObservationContext.Provider>
        </div>
      )}
    </div>
  );
};

export default SlideContainer;

const wrapperStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  overflow: 'hidden',
  background: '#12100c'
};

const panelStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  overflow: 'hidden'
};

/**
 * What makes the swipe survive a real device rather than only a desktop browser.
 *
 * In a Capacitor WebView (and mobile Safari/Chrome) a horizontal drag is
 * contested: the engine wants it for scrolling, overscroll chaining and
 * navigation gestures, and it claims the gesture before any JS handler can
 * argue — touchmove listeners are passive there, so preventDefault() is not an
 * option. Declaring the intent in CSS is:
 *
 *   touch-action: pan-y   — vertical panning stays native; HORIZONTAL gestures
 *                           are ours, so the drag isn't stolen mid-swipe.
 *   overscroll-behavior   — no pull-to-refresh or rubber-band chaining when a
 *                           swipe starts near an edge.
 *   user-select: none     — Android otherwise starts a text selection during a
 *                           drag across the felt, which fights the gesture.
 *   -webkit-touch-callout — long-press on the felt must not raise iOS's
 *                           copy/share callout mid-drag.
 */
const tableSwipeSurfaceStyle: CSSProperties = {
  touchAction: 'pan-y',
  overscrollBehavior: 'contain',
  userSelect: 'none',
  WebkitUserSelect: 'none',
  WebkitTouchCallout: 'none'
} as CSSProperties;
